import { ReactElement, useEffect, useState, useContext } from 'react'
import MapGl, { Marker, useMap, GeolocateControl } from 'react-map-gl'
import styled, { ThemeContext } from 'styled-components'
import { device } from '../breakpoints'
import IcomoonReact from 'icomoon-react'
import iconSet from './selection.json'

const isMobile = window.innerWidth < 768
// Fly to the active marker
const FlyManager = ({ lat, lng }: { lat: number; lng: number }) => {
  const { current: map } = useMap()

  if (lat && lng && map) {
    map.flyTo({ center: [lng, lat] })
  }

  return <></>
}

const Map = ({
  handleMarkerClick,
  markers,
  handleMapLoaded,
  handleUserLocationFound,
  handleUserLocationError,
  activeMarker,
  locked,
  publisherDomain,
  locateMe,
}: {
  handleMarkerClick: (marker: any) => void
  markers: any[]
  handleMapLoaded: () => void
  handleUserLocationFound: (lat: number, lng: number) => void
  handleUserLocationError: (e: GeolocationPositionError) => void
  activeMarker: any
  locked: boolean
  publisherDomain: string | undefined
  locateMe: boolean
}): ReactElement => {
  const [geolocateControlRef, setGeolocateControlRef] = useState<any>(null)
  const themeContext = useContext(ThemeContext)

  // Get the user location
  useEffect(() => {
    if (locateMe && geolocateControlRef) {
      console.log('Trigger geolocateControl')
      geolocateControlRef.trigger()
    }
  }, [locateMe, geolocateControlRef])

  // We need to center and zoom the map according to the tenant
  let center = [6.57603791111531, 46.542555273891324]
  // if (publisherDomain === 'tdg') center = [6.148, 46.3]
  // else if (publisherDomain === 'tagesanzeiger') center = [8.542, 47.375]

  let zoom = isMobile ? 11.5 : 12.5

  const popRangeMin = Math.min(...markers.map((marker) => marker.pop))
  const popRangeMax = Math.max(...markers.map((marker) => marker.pop))

  const mapStyle =
    themeContext.colorMode === 'light'
      ? 'clpr0qk7x015j01p97k0m5ial'
      : 'clpr0qk7x015j01p97k0m5ial'

  return (
    <Container>
      <MapGl
        scrollZoom={!locked}
        dragPan={!locked}
        pitchWithRotate={false}
        dragRotate={false}
        // touchZoomRotate={false}
        onLoad={() => {
          handleMapLoaded()
        }}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom: zoom,
        }}
        minZoom={10}
        maxZoom={15}
        style={{ width: '100%', height: '100%' }}
        mapStyle={`mapbox://styles/ta-interaktiv/${mapStyle}`}
        mapboxAccessToken='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJja292ZWk5ZnIwN2w0MndwZWQ0Nmpqcm1iIn0.d1OaviBwQQGO1_roYeaimQ'
      >
        <GeolocateControl
          ref={setGeolocateControlRef}
          position='bottom-right'
          onGeolocate={(res) => {
            handleUserLocationFound(res.coords.latitude, res.coords.longitude)
          }}
          onError={(e: GeolocationPositionError) => {
            handleUserLocationError(e)
          }}
        />
        {activeMarker && (
          <FlyManager
            lat={activeMarker.latitude}
            lng={activeMarker.longitude}
          />
        )}
        {markers.map((marker) => (
          <Marker
            key={marker.markerId}
            longitude={marker.longitude}
            latitude={marker.latitude}
            anchor='center'
            style={{
              zIndex: marker.markerId === activeMarker?.markerId ? 1 : 0,
            }}
          >
            <MarkerContent
              active={marker.markerId === activeMarker?.markerId}
              category={marker.category ? marker.category : 'default'}
              size={(marker.pop - 0) * (50 - 0) / (popRangeMax - popRangeMin) + 40}
              onClick={() => handleMarkerClick(marker)}
            >
              <IcomoonReact
                iconSet={iconSet}
                icon='office'
                size={16}
                color={
                  marker.markerId === activeMarker?.markerId
                    ? '#ffffff'
                    : themeContext.textColor
                }
                opacity={
                  marker.until_2025 ||
                  marker.markerId === activeMarker?.markerId
                    ? 1
                    : themeContext.colorMode === 'dark'
                    ? 0.4
                    : 0.35
                }
              ></IcomoonReact>
            </MarkerContent>
          </Marker>
        ))}
      </MapGl>
      <Legend style={{ display: activeMarker ? 'none' : 'block' }}>
        <LegendItem>
          <MarkerContent active={false} category='legend-item' size={1}>
            <IcomoonReact
              iconSet={iconSet}
              icon='office'
              size={16}
              color={themeContext.textColor}
              opacity={1}
            ></IcomoonReact>
          </MarkerContent>Prévu d’ici 2025
        </LegendItem>

        <LegendItem>
          <MarkerContent active={false} category='legend-item' size={1}>
            <IcomoonReact
              iconSet={iconSet}
              icon='office'
              size={16}
              color={themeContext.textColor}
              opacity={themeContext.colorMode === 'dark' ? 0.4 : 0.35}
            ></IcomoonReact>
          </MarkerContent>Prévu après 2025
        </LegendItem>
      </Legend>
    </Container>
  )
}

export default Map

const MarkerContent = styled.div<{
  active: boolean
  category: string
  size: number
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ active, theme }) =>
    active ? theme.mainColor : theme.plainColor};
  width: ${({ size }) => 40}px;
  height: ${({ size }) => 40}px;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  ${({ category }) => category === 'default' && 'cursor: pointer;'}

  // &:before {
  //   z-index: -1;
  //   content: '';
  //   background-color: #ffffff44;
  //   width: ${({ size }) => size * 1}px;
  //   height: ${({ size }) => size * 1}px;
  //   border-radius: 50%;
  //   top: 50%;
  //   left: 50%;
  //   position: absolute;
  //   transform: translate(-50%, -50%);
  // }
`


const Legend = styled.div`
background-color: var(--site-background);
  position: absolute;
  left: 0;
  top: 108px;
  padding: 10px;
  @media (max-width: 768px) {
    top: 165px;
  }
  background-color: ${({ theme }) => theme.plainColor};
  font-family: var(--font-sans-serif);
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.5em;
  font-size: 0.75em;

  ${MarkerContent} {
    font-size: 0.75em;
    margin-right: 0.5em;
    box-shadow: none;
    width: 24px;
    height: 24px;
  }
`

const Container = styled.div`
  position: relative;
  background-color: #f5f6f7;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    margin-left: 0%;
    height: 100%;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 100%;
    margin-left: 0%;
    height: 100vh;
  }
`